import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Search, Clock, Users, TrendingUp, ChevronLeft, ChevronRight, Eye, Zap } from 'lucide-react'
import { adminService } from '@/lib/api/services/admin.service'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 10;

interface SearchHistoryItem {
  id: string
  client_profile_id: string
  search_query: string
  results_count: number
  result_profile_ids: string[]
  result_similarities: Record<string, number>
  search_latency_ms: number
  search_method: 'semantic' | 'fallback'
  embedding_model: string
  match_threshold: number
  created_at: string
  client_profiles?: {
    company_name: string
    user_id: string
  }
}

// Stat card component
function StatCard({
  icon: Icon,
  count,
  label,
  iconBgClass = 'bg-[rgba(70,105,237,0.10)]'
}: {
  icon: React.ElementType;
  count: number | string;
  label: string;
  iconBgClass?: string;
}) {
  return (
    <div className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] flex flex-col items-center justify-center">
      <div className={cn("w-16 h-16 rounded-full flex items-center justify-center mb-4", iconBgClass)}>
        <Icon className="w-6 h-6 text-[#4669ed]" />
      </div>
      <div className="text-[40px] leading-[42px] font-normal text-slate-900 tracking-tight">
        {count}
      </div>
      <div className="text-sm text-slate-500 mt-2">
        {label}
      </div>
    </div>
  );
}

// Method badge component
function MethodBadge({ method }: { method: 'semantic' | 'fallback' }) {
  if (method === 'semantic') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[rgba(70,105,237,0.10)] text-[#4669ed]">
        <Zap className="w-3 h-3" />
        Semantic
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
      Fallback
    </span>
  );
}

export default function SearchHistory() {
  const [selectedSearch, setSelectedSearch] = useState<SearchHistoryItem | null>(null)
  const [page, setPage] = useState(1)

  const { data: searches, isLoading } = useQuery({
    queryKey: ['admin', 'search-history'],
    queryFn: async () => {
      return await adminService.getSearchHistory(100) as SearchHistoryItem[]
    },
  })

  const { data: freelancerProfiles } = useQuery<Array<{ id: string; display_name: string | null }>>({
    queryKey: ['admin', 'freelancer-profiles-for-search'],
    queryFn: async () => {
      // TODO: Create backend endpoint for fetching freelancer profiles for search history
      // For now, we'll need to keep this query or create a new endpoint
      // This is a temporary solution - the backend should provide this data
      return []
    },
    enabled: !!selectedSearch,
  })

  const getFreelancerProfile = (profileId: string) => {
    return freelancerProfiles?.find(p => p.id === profileId)
  }

  // Pagination
  const totalCount = searches?.length || 0
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)
  const paginatedSearches = searches?.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  ) || []

  // Stats
  const stats = useMemo(() => {
    if (!searches) return { total: 0, semantic: 0, avgLatency: 0, avgResults: 0 };

    const semanticCount = searches.filter(s => s.search_method === 'semantic').length;
    const totalLatency = searches.reduce((acc, s) => acc + s.search_latency_ms, 0);
    const totalResults = searches.reduce((acc, s) => acc + s.results_count, 0);

    return {
      total: searches.length,
      semantic: semanticCount,
      avgLatency: searches.length > 0 ? Math.round(totalLatency / searches.length) : 0,
      avgResults: searches.length > 0 ? Math.round((totalResults / searches.length) * 10) / 10 : 0,
    };
  }, [searches]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
        <p className="text-slate-500">Loading search history...</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Stat Cards */}
      <div className="flex gap-6">
        <StatCard icon={Search} count={stats.total} label="Total Searches" />
        <StatCard icon={Zap} count={stats.semantic} label="Semantic Searches" />
        <StatCard icon={Clock} count={`${stats.avgLatency}ms`} label="Avg Latency" />
        <StatCard icon={Users} count={stats.avgResults} label="Avg Results" />
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-[40px] border border-slate-200 overflow-hidden shadow-sm">
        {/* Table Header */}
        <div className="flex items-center justify-between px-6 py-6 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">
            Recent Searches ({totalCount})
          </h3>
        </div>

        {/* Table */}
        {paginatedSearches.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            No search history found.
          </div>
        ) : (
          <>
            {/* Column Headers */}
            <div className="bg-slate-50 border-b border-slate-200">
              <div className="grid grid-cols-[1.5fr_2fr_1fr_1fr_1fr_1fr_1fr] px-6 py-4">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Client</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Query</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Results</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Method</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Latency</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Date</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</div>
              </div>
            </div>

            {/* Table Rows */}
            <div>
              {paginatedSearches.map((search) => (
                <div
                  key={search.id}
                  className="grid grid-cols-[1.5fr_2fr_1fr_1fr_1fr_1fr_1fr] px-6 py-4 border-b border-slate-100 items-center hover:bg-slate-50/50"
                >
                  {/* Client */}
                  <div>
                    <Link
                      to={`/client/${search.client_profile_id}`}
                      className="text-sm font-semibold text-[#4669ed] hover:underline"
                    >
                      {search.client_profiles?.company_name || 'Unknown Client'}
                    </Link>
                  </div>

                  {/* Query */}
                  <div className="text-sm text-slate-700 line-clamp-2">
                    {search.search_query.length > 80
                      ? search.search_query.substring(0, 80) + '...'
                      : search.search_query}
                  </div>

                  {/* Results */}
                  <div className="flex items-center gap-1">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-xs font-semibold text-slate-700">
                      <Users className="w-3 h-3" />
                      {search.results_count}
                    </span>
                  </div>

                  {/* Method */}
                  <div>
                    <MethodBadge method={search.search_method} />
                  </div>

                  {/* Latency */}
                  <div className="flex items-center gap-1 text-sm text-slate-700">
                    <Clock className="w-4 h-4 text-slate-400" />
                    {search.search_latency_ms}ms
                  </div>

                  {/* Date */}
                  <div className="text-sm text-slate-700">
                    {format(new Date(search.created_at), 'MMM d, HH:mm')}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedSearch(search)}
                      className="w-8 h-8 rounded-md bg-[rgba(70,105,237,0.10)] flex items-center justify-center text-[#4669ed] hover:bg-[rgba(70,105,237,0.20)]"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-6 border-t border-slate-200">
            <div className="text-sm text-slate-500">
              Showing {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount} searches
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {(() => {
                const maxVisible = 5;
                let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
                const endPage = Math.min(totalPages, startPage + maxVisible - 1);
                startPage = Math.max(1, endPage - maxVisible + 1);
                return Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                  const pageNum = startPage + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={cn(
                        "w-8 h-8 rounded-md border flex items-center justify-center text-sm font-bold",
                        page === pageNum
                          ? "bg-[#4669ed] border-[#4669ed] text-white"
                          : "border-slate-200 text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      {pageNum}
                    </button>
                  );
                });
              })()}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Search Details Dialog */}
      <Dialog open={!!selectedSearch} onOpenChange={(open) => !open && setSelectedSearch(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto rounded-[24px]">
          <DialogHeader>
            <DialogTitle>Search Details</DialogTitle>
            <DialogDescription>
              Full details of the search performed by {selectedSearch?.client_profiles?.company_name || 'Unknown Client'}
            </DialogDescription>
          </DialogHeader>

          {selectedSearch && (
            <div className="space-y-6">
              {/* Query */}
              <div className="bg-slate-50 rounded-[20px] p-4">
                <h3 className="font-semibold text-sm text-slate-500 uppercase tracking-wider mb-2">Search Query</h3>
                <p className="text-slate-900 whitespace-pre-wrap">
                  {selectedSearch.search_query}
                </p>
              </div>

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-[20px] p-4">
                  <h3 className="font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">Search Metadata</h3>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Method:</dt>
                      <dd><MethodBadge method={selectedSearch.search_method} /></dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Model:</dt>
                      <dd className="font-medium text-slate-900">{selectedSearch.embedding_model}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Threshold:</dt>
                      <dd className="font-medium text-slate-900">{selectedSearch.match_threshold}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Latency:</dt>
                      <dd className="font-medium text-slate-900">{selectedSearch.search_latency_ms}ms</dd>
                    </div>
                  </dl>
                </div>

                <div className="bg-slate-50 rounded-[20px] p-4">
                  <h3 className="font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">Performance</h3>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Results Count:</dt>
                      <dd className="font-medium text-slate-900">{selectedSearch.results_count}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Timestamp:</dt>
                      <dd className="font-medium text-slate-900">{format(new Date(selectedSearch.created_at), 'PPpp')}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Results */}
              <div>
                <h3 className="font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">Results Returned</h3>
                <div className="space-y-2">
                  {selectedSearch.result_profile_ids.map((profileId) => {
                    const similarity = selectedSearch.result_similarities[profileId]
                    const freelancerProfile = getFreelancerProfile(profileId)
                    return (
                      <div
                        key={profileId}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-[16px]"
                      >
                        {freelancerProfile?.id ? (
                          <Link
                            to={`/genius/${profileId}`}
                            className="text-sm font-semibold text-[#4669ed] hover:underline"
                          >
                            {freelancerProfile.display_name || 'Unknown Genius'}
                          </Link>
                        ) : (
                          <span className="text-sm text-slate-700">Unknown Genius</span>
                        )}
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[rgba(70,105,237,0.10)] text-xs font-semibold text-[#4669ed]">
                          <TrendingUp className="w-3 h-3" />
                          {similarity ? `${Math.round(similarity * 100)}%` : 'N/A'}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
